import { Container, type ContainerOptions } from "../core/Container.js";
import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import { rectContains, type Point, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { Panel } from "./Panel.js";
import type { Orientation } from "./ScrollBar.js";

/** Which pane keeps its size when the whole control is resized. */
export type FixedPane = "none" | "panel1" | "panel2";

export interface SplitContainerOptions extends ContainerOptions {
  /**
   * `vertical` puts the bar itself upright, so the panes sit side by side.
   * This is the WinForms sense of the word, and it names the bar rather than
   * the arrangement.
   */
  orientation?: Orientation;
  /** Distance from the near edge to the near side of the bar. */
  splitterPosition?: number;
  /** Thickness of the bar. Defaults to the theme's `splitterSize`. */
  splitterWidth?: number;
  /** Smallest each pane may be dragged to. */
  panel1MinSize?: number;
  panel2MinSize?: number;
  /** Which pane holds its size across a resize. */
  fixedPane?: FixedPane;
  /** Refuse to move the bar at all. */
  splitterFixed?: boolean;
  /** Hide one pane and give the whole area to the other. */
  collapsed?: FixedPane;
}

const KEY_STEP = 8;
const PAGE_STEP = 48;

/**
 * Two panes with a draggable bar between them.
 *
 * Named after the WinForms control rather than the older `Splitter`, which
 * resized whatever it was docked against. Webwin has no docking — layout is
 * manual bounds plus anchors — so a bare splitter would have nothing to
 * resize. Owning both panes makes the relationship explicit.
 *
 * `panel1` and `panel2` are `Panel`s the control creates and sizes, so an
 * application fills them and never positions them. Nest two of these for a
 * three-pane layout, which is how the shell demo builds its workspace.
 */
export class SplitContainer extends Container {
  readonly panel1: Panel;
  readonly panel2: Panel;

  #position: number;
  #width: number | null;
  #dragging = false;
  #hovered = false;
  #dragOffset = 0;
  #collapsed: FixedPane;

  #orientation: Orientation = "vertical";
  panel1MinSize: number;
  panel2MinSize: number;
  fixedPane: FixedPane;
  splitterFixed: boolean;

  constructor(options: SplitContainerOptions = {}) {
    super({ width: 400, height: 260, tabStop: true, ...options });
    // Assigned past the setter: it re-clamps the position, which does not exist yet.
    this.#orientation = options.orientation ?? "vertical";
    this.panel1MinSize = options.panel1MinSize ?? 24;
    this.panel2MinSize = options.panel2MinSize ?? 24;
    this.fixedPane = options.fixedPane ?? "none";
    this.splitterFixed = options.splitterFixed ?? false;
    this.#width = options.splitterWidth ?? null;
    this.#collapsed = options.collapsed ?? "none";
    this.#position = options.splitterPosition ?? Math.round(this.#extent / 2);

    this.panel1 = this.controls.add(new Panel({ style: "none" }));
    this.panel2 = this.controls.add(new Panel({ style: "none" }));

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => this.#endDrag());
    this.on("pointerleave", () => this.#setHovered(false));
    this.on("keydown", (e) => this.#onKeyDown(e));
    this.performLayout();
  }

  // --- geometry ---------------------------------------------------------

  /**
   * Which way the bar runs: "vertical" for a bar top to bottom, with the panes side by side.
   *
   * An accessor rather than a field because the position is measured along whichever axis the
   * bar divides - so turning the split re-points the same number at a different edge, and that
   * number may not even fit the new one.
   */
  get orientation(): Orientation {
    return this.#orientation;
  }
  set orientation(v: Orientation) {
    if (this.#orientation === v) return;
    this.#orientation = v;
    this.#position = this.clampPosition(this.#position);
    this.performLayout();
    this.invalidate();
  }

  /** True when the bar runs top to bottom and the panes sit side by side. */
  get vertical(): boolean {
    return this.#orientation === "vertical";
  }

  /** Length of the axis the bar slides along. */
  get #extent(): number {
    return this.vertical ? this.width : this.height;
  }

  get splitterWidth(): number {
    return this.#width ?? this.desktop?.theme.metrics.splitterSize ?? 6;
  }
  set splitterWidth(v: number) {
    this.#width = Math.max(1, v);
    this.performLayout();
    this.invalidate();
  }

  /** Distance from the near edge to the near side of the bar. */
  get splitterPosition(): number {
    return this.#position;
  }
  set splitterPosition(v: number) {
    const next = this.clampPosition(v);
    if (next === this.#position) return;
    this.#position = next;
    this.performLayout();
    this.invalidate();
    this.emit("splittermoved");
  }

  /** The nearest legal position, honoring both minimums. */
  clampPosition(v: number): number {
    const room = this.#extent - this.splitterWidth;
    if (room <= 0) return 0;
    const lo = Math.min(this.panel1MinSize, room);
    const hi = Math.max(lo, room - this.panel2MinSize);
    return Math.round(Math.min(hi, Math.max(lo, v)));
  }

  /** Which pane, if either, is currently hidden. */
  get collapsed(): FixedPane {
    return this.#collapsed;
  }
  set collapsed(v: FixedPane) {
    if (this.#collapsed === v) return;
    this.#collapsed = v;
    this.performLayout();
    this.invalidate();
    this.emit("collapsedchanged");
  }

  /** The bar's rectangle, or null while a pane is collapsed. */
  get splitterRect(): Rect | null {
    if (this.#collapsed !== "none") return null;
    const w = this.splitterWidth;
    return this.vertical
      ? { x: this.#position, y: 0, width: w, height: this.height }
      : { x: 0, y: this.#position, width: this.width, height: w };
  }

  /** Where `panel1` sits, in local coordinates. */
  get panel1Rect(): Rect {
    if (this.#collapsed === "panel1") return { x: 0, y: 0, width: 0, height: 0 };
    if (this.#collapsed === "panel2") return this.localRect;
    return this.vertical
      ? { x: 0, y: 0, width: this.#position, height: this.height }
      : { x: 0, y: 0, width: this.width, height: this.#position };
  }

  /** Where `panel2` sits, in local coordinates. */
  get panel2Rect(): Rect {
    if (this.#collapsed === "panel2") return { x: 0, y: 0, width: 0, height: 0 };
    if (this.#collapsed === "panel1") return this.localRect;
    const near = this.#position + this.splitterWidth;
    return this.vertical
      ? { x: near, y: 0, width: Math.max(0, this.width - near), height: this.height }
      : { x: 0, y: near, width: this.width, height: Math.max(0, this.height - near) };
  }

  // --- layout -----------------------------------------------------------

  /**
   * Anchors are meaningless here: the two panes are placed by this control,
   * and nothing else is a child.
   */
  protected override applyAnchors(): void {}

  protected override onResize(): void {
    this.#keepFixedPane();
    this.performLayout();
  }

  /** Last extent a resize was measured against, so `fixedPane` has a delta. */
  #lastExtent = 0;

  #keepFixedPane(): void {
    const extent = this.#extent;
    const previous = this.#lastExtent;
    this.#lastExtent = extent;
    if (previous === 0 || previous === extent) return;
    // panel1 fixed: the bar stays put and panel2 absorbs the change, which is
    // what leaving the position alone already does.
    if (this.fixedPane === "panel2") this.#position += extent - previous;
    else if (this.fixedPane === "none") this.#position = Math.round(this.#position * (extent / previous));
    this.#position = this.clampPosition(this.#position);
  }

  protected override performLayout(): void {
    // Container's constructor reaches here before the panels exist.
    if (!this.panel1 || !this.panel2) return;
    this.#lastExtent = this.#extent;
    this.#position = this.clampPosition(this.#position);
    const a = this.panel1Rect;
    const b = this.panel2Rect;
    this.panel1.setBounds(a.x, a.y, a.width, a.height);
    this.panel2.setBounds(b.x, b.y, b.width, b.height);
    this.panel1.visible = this.#collapsed !== "panel1";
    this.panel2.visible = this.#collapsed !== "panel2";
  }

  protected override onEnvironmentChanged(): void {
    // A theme swap can change splitterSize, which moves both panes.
    this.performLayout();
  }

  // --- input ------------------------------------------------------------

  /** True when `p` is on the bar and the bar can actually be dragged. */
  isOnSplitter(p: Point): boolean {
    if (this.splitterFixed) return false;
    const r = this.splitterRect;
    return r !== null && rectContains(r, p);
  }

  override cursorAt(p: Point): string {
    if (this.#dragging || this.isOnSplitter(p)) {
      return this.vertical ? "col-resize" : "row-resize";
    }
    return super.cursorAt(p);
  }

  #setHovered(v: boolean): void {
    if (this.#hovered === v) return;
    this.#hovered = v;
    this.invalidate();
  }

  #onPointerDown(e: MouseEventArgs): void {
    if (this.inputBlocked || e.button !== 0) return;
    const p = { x: e.x, y: e.y };
    if (!this.isOnSplitter(p)) return;
    e.handled = true;
    this.focus();
    this.#dragging = true;
    this.#dragOffset = (this.vertical ? e.x : e.y) - this.#position;
    this.desktop?.input.setCapture(this);
    this.invalidate();
  }

  #onPointerMove(e: MouseEventArgs): void {
    const p = { x: e.x, y: e.y };
    if (this.#dragging) {
      this.splitterPosition = (this.vertical ? e.x : e.y) - this.#dragOffset;
      return;
    }
    this.#setHovered(this.isOnSplitter(p));
  }

  #endDrag(): void {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.desktop?.input.releaseCapture(this);
    this.invalidate();
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (!this.focused || this.splitterFixed || this.#collapsed !== "none") return;
    const along = this.vertical ? ["ArrowLeft", "ArrowRight"] : ["ArrowUp", "ArrowDown"];
    let delta = 0;
    if (e.key === along[0]) delta = -KEY_STEP;
    else if (e.key === along[1]) delta = KEY_STEP;
    else if (e.key === "PageUp") delta = -PAGE_STEP;
    else if (e.key === "PageDown") delta = PAGE_STEP;
    else if (e.key === "Home") {
      this.splitterPosition = 0;
      e.handled = true;
      return;
    } else if (e.key === "End") {
      this.splitterPosition = this.#extent;
      e.handled = true;
      return;
    } else return;

    this.splitterPosition = this.#position + delta;
    e.handled = true;
  }

  // --- painting ---------------------------------------------------------

  /** The panes paint themselves; the bar goes on top, in the overlay pass. */
  protected override onPaint(): void {}

  protected override onPaintOverlay(rc: RenderContext, theme: Theme): void {
    const r = this.splitterRect;
    if (!r) return;
    theme.drawSplitter(rc, r, {
      hovered: this.#hovered && !this.splitterFixed,
      pressed: this.#dragging,
      dragging: this.#dragging,
      focused: this.focused,
      disabled: this.splitterFixed || !this.effectivelyEnabled,
      vertical: this.vertical,
    });
  }

  override handlesDragAt(p: Point): boolean {
    return this.isOnSplitter(p);
  }
}
